import { useState, useEffect, useRef } from "react";

// ── Stroop Player Controller — Не верь словам ─────────────────────────────────

// Цвета приходят динамически из question.colors (бэкенд шлёт актуальный список)
// Фолбэк на случай если вопрос ещё не пришёл
const FALLBACK_COLORS = [
  { id: "red",    label: "КРАСНЫЙ",   hex: "#FF3355" },
  { id: "blue",   label: "СИНИЙ",     hex: "#00AAFF" },
  { id: "green",  label: "ЗЕЛЁНЫЙ",   hex: "#3DFF3D" },
  { id: "yellow", label: "ЖЁЛТЫЙ",    hex: "#FFE600" },
  { id: "orange", label: "ОРАНЖЕВЫЙ", hex: "#FF8C00" },
  { id: "purple", label: "ФИОЛЕТОВЫЙ",hex: "#C77DFF" },
];

function LivesRow({ lives, max = 3 }) {
  return (
    <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
      {Array.from({ length: max }).map((_, i) => (
        <span key={i} style={{ fontSize:"var(--fs-xl)", opacity: i < (lives ?? max) ? 1 : 0.2 }}>
          {i < (lives ?? max) ? "❤️" : "🖤"}
        </span>
      ))}
    </div>
  );
}

export default function StroopPlayer({
  question, result, myLives, myScore, playerName, onAnswer,
}) {
  const [answered, setAnswered] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const prevQRef = useRef(null);

  // Сброс при каждом новом вопросе.
  // prevQRef позволяет детектировать как смену номера (следующий вопрос),
  // так и повтор qnum=1 (новая игра после рестарта).
  useEffect(() => {
    if (!question) {
      // question стал null — игра сброшена, чистим всё
      prevQRef.current = null;
      setAnswered(null);
      setTimeLeft(null);
      return;
    }
    // Новый вопрос или новая игра (qnum мог вернуться к 1)
    if (question.qnum !== prevQRef.current) {
      prevQRef.current = question.qnum;
      setAnswered(question.already_answered ? (question.my_answer || "__answered__") : null);
      setTimeLeft(question.time);
    } else if (question.already_answered) {
      // Same round after Socket.IO reconnect: keep the answer locked instead of
      // showing tappable buttons that the server will silently reject.
      setAnswered(question.my_answer || "__answered__");
    }
  }, [question]);

  // Countdown
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;
    const t = setTimeout(() => setTimeLeft(n => Math.max(0, n - 1)), 1000);
    return () => clearTimeout(t);
  }, [timeLeft]);

  const isEliminated = (myLives ?? 3) <= 0;

  const handleAnswer = (colorId) => {
    if (answered || isEliminated || result) return;
    setAnswered(colorId);
    onAnswer(colorId);
  };

  // ── Eliminated ────────────────────────────────────────────────────────────
  if (isEliminated) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 animate-slide-up">
        <div style={{ fontSize: "4rem" }}>💀</div>
        <p className="font-display text-white" style={{ fontSize:"var(--fs-xl)" }}>Выбыл!</p>
        <p className="font-heading text-[#555]" style={{ fontSize:"var(--fs-sm)" }}>
          Наблюдай за остальными
        </p>
        <div style={{
          background: "#1a1030", border: "2px solid #333",
          borderRadius: "1rem", padding: "0.75rem 1.5rem", textAlign: "center",
        }}>
          <p className="font-heading text-[#555]" style={{ fontSize:"var(--fs-xs)" }}>Твои очки</p>
          <p className="font-display text-white" style={{ fontSize:"var(--fs-2xl)" }}>{myScore || 0}</p>
        </div>
      </div>
    );
  }

  // ── Result feedback ───────────────────────────────────────────────────────
  if (result) {
    const myAns = result.answers[playerName];
    const isCorrect = myAns === result.correct;
    const noAnswer = !myAns;
    const pts = result.pts[playerName];
    const lives = result.lives[playerName] ?? 3;

    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 animate-slide-up">
        <div style={{ fontSize: "3.5rem" }}>
          {isCorrect ? "✅" : noAnswer ? "⏱️" : "❌"}
        </div>
        <p className="font-display" style={{
          fontSize:"var(--fs-2xl)",
          color: isCorrect ? "#3DFF3D" : noAnswer ? "#FFE600" : "#FF3355",
          textShadow: `0 0 16px ${isCorrect ? "#3DFF3D" : noAnswer ? "#FFE600" : "#FF3355"}`,
        }}>
          {isCorrect ? "ПРАВИЛЬНО!" : noAnswer ? "ВРЕМЯ!" : "НЕВЕРНО!"}
        </p>

        {/* What the word actually was */}
        <div style={{
          background: "#0a0a14",
          border: `2px solid ${result.ink.hex}55`,
          borderRadius: "1rem",
          padding: "0.75rem 1.5rem",
          textAlign: "center",
        }}>
          <p className="font-heading text-[#555]" style={{ fontSize:"var(--fs-xs)" }}>Цвет текста был:</p>
          <p className="font-display" style={{ color: result.ink.hex, fontSize:"var(--fs-2xl)" }}>
            {result.ink.label}
          </p>
        </div>

        {pts > 0 && (
          <p className="font-display text-[#3DFF3D]" style={{ fontSize:"var(--fs-xl)" }}>+{pts} очков</p>
        )}

        <LivesRow lives={lives} max={3} />

        <p className="font-heading text-[#555]" style={{ fontSize:"var(--fs-sm)" }}>
          Всего: {result.scores[playerName] || 0} очков
        </p>
      </div>
    );
  }

  // ── Question ──────────────────────────────────────────────────────────────
  if (!question) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3">
        <p className="font-heading text-[#555]" style={{ fontSize:"var(--fs-md)" }}>Ждём вопрос...</p>
      </div>
    );
  }

  const COLORS = question?.colors || FALLBACK_COLORS;
  const gridCols = COLORS.length <= 4 ? "1fr 1fr" : "1fr 1fr 1fr";

  const timerPct = timeLeft / question.time;
  const timerColor = timerPct > 0.5 ? "#3DFF3D" : timerPct > 0.25 ? "#FFE600" : "#FF3355";

  return (
    <div className="flex-1 flex flex-col gap-3 animate-slide-up">
      {/* Timer + score row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <LivesRow lives={myLives ?? 3} max={3} />
        <div style={{
          width: "3rem", height: "3rem",
          borderRadius: "50%",
          border: `3px solid ${timerColor}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: `0 0 12px ${timerColor}88`,
          transition: "border-color 0.3s, box-shadow 0.3s",
        }}>
          <span className="font-display" style={{ color: timerColor, fontSize:"var(--fs-xl)" }}>
            {timeLeft}
          </span>
        </div>
        <span className="font-display text-[#555]" style={{ fontSize:"var(--fs-sm)" }}>
          {myScore || 0} <span style={{ fontSize:"var(--fs-xs)" }}>очков</span>
        </span>
      </div>

      {/* THE WORD in wrong color */}
      <div style={{
        background: "#0a0a14",
        border: `2px solid ${question.ink.hex}44`,
        borderRadius: "1.25rem",
        padding: "1.25rem",
        textAlign: "center",
        boxShadow: `0 0 30px ${question.ink.hex}18`,
      }}>
        <p className="font-heading text-[#444]" style={{ fontSize:"var(--fs-xs)", marginBottom: "0.4rem" }}>
          {question.task_type === "word"
            ? <span style={{ color: "#FFE600", fontWeight: "bold" }}>⚠️ ПРОЧИТАЙ СЛОВО!</span>
            : "Какого цвета этот текст?"}
        </p>
        <p className="font-display" style={{
          fontSize:"var(--fs-3xl)",
          color: question.ink.hex,
          textShadow: `0 0 20px ${question.ink.hex}`,
          letterSpacing: "0.05em",
          lineHeight: 1.1,
        }}>
          {question.word.label}
        </p>
      </div>

      {/* Color buttons */}
      <div style={{
        display: "grid",
        gridTemplateColumns: gridCols,
        gap:"var(--gap-md)",
        flex: 1,
      }}>
        {COLORS.map(color => {
          const isChosen = answered === color.id;
          const isDisabled = !!answered;

          return (
            <button
              key={color.id}
              onClick={() => handleAnswer(color.id)}
              style={{
                background: isChosen
                  ? color.hex + "33"
                  : "#0a0a14",
                border: `3px solid ${isChosen ? color.hex : color.hex + "55"}`,
                borderRadius: "1.1rem",
                padding: "0",
                minHeight: "5.5rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap:"var(--gap-sm)",
                cursor: isDisabled ? "default" : "pointer",
                boxShadow: isChosen
                  ? `0 0 20px ${color.hex}55, 4px 4px 0 #000`
                  : "4px 4px 0 #000",
                opacity: isDisabled && !isChosen ? 0.45 : 1,
                transform: isChosen ? "scale(0.97) translateY(2px)" : "scale(1)",
                transition: "all 0.1s ease",
                WebkitTapHighlightColor: "transparent",
                touchAction: "manipulation",
              }}
            >
              {/* Color dot */}
              <div style={{
                width: "1.4rem", height: "1.4rem",
                borderRadius: "50%",
                background: color.hex,
                boxShadow: `0 0 10px ${color.hex}`,
              }} />
              <span className="font-display" style={{
                color: color.hex,
                fontSize:"var(--fs-md)",
                textShadow: isChosen ? `0 0 12px ${color.hex}` : "none",
              }}>
                {color.label}
              </span>
            </button>
          );
        })}
      </div>

      {answered && (
        <p className="text-center font-heading text-[#555]" style={{ fontSize:"var(--fs-xs)" }}>
          Ждём всех игроков...
        </p>
      )}
    </div>
  );
}